import React, {
  Fragment,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";

import PostDialog from "../PostDialog/PostDialog";
import SkeletonLoader from "../SkeletonLoader/SkeletonLoader";

const getPostId = (post) => {
  if (!post) {
    return null;
  }
  const identifier = post._id || post.postId;
  return typeof identifier === "string" ? identifier : identifier?.toString() || null;
};

const SwipeFeed = ({
  posts,
  fetching,
  hasMore,
  onLoadMore,
  onPostDialogDispatch,
}) => {
  const containerRef = useRef(null);
  const intersectionObserver = useRef(null);
  const [viewportHeight, setViewportHeight] = useState(() =>
    typeof window !== "undefined" ? window.innerHeight : 0
  );
  const [activeIndex, setActiveIndex] = useState(0);

  const updateViewportHeight = useCallback(() => {
    if (typeof window === "undefined") {
      return;
    }
    setViewportHeight(window.innerHeight);
  }, []);

  useEffect(() => {
    updateViewportHeight();
    if (typeof window === "undefined") {
      return undefined;
    }
    window.addEventListener("resize", updateViewportHeight);
    return () => window.removeEventListener("resize", updateViewportHeight);
  }, [updateViewportHeight]);

  useEffect(() => {
    updateViewportHeight();
  }, [posts.length, updateViewportHeight]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return undefined;
    }
    const items = Array.from(
      container.querySelectorAll("[data-feed-index]")
    );
    if (!items.length) {
      return undefined;
    }
    intersectionObserver.current?.disconnect();
    const observer = new IntersectionObserver(
      (entries) => {
        const visibleEntries = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visibleEntries.length > 0) {
          const index = Number(visibleEntries[0].target.dataset.feedIndex);
          if (!Number.isNaN(index)) {
            setActiveIndex(index);
          }
        }
      },
      {
        threshold: 0.55,
      }
    );
    items.forEach((item) => observer.observe(item));
    intersectionObserver.current = observer;
    return () => observer.disconnect();
  }, [posts]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return undefined;
    }
    let ticking = false;
    const onScroll = () => {
      if (ticking) {
        return;
      }
      ticking = true;
      window.requestAnimationFrame(() => {
        ticking = false;
        if (!hasMore || fetching) {
          return;
        }
        const { scrollTop, scrollHeight, clientHeight } = container;
        if (scrollHeight - (scrollTop + clientHeight) < clientHeight) {
          onLoadMore();
        }
      });
    };
    container.addEventListener("scroll", onScroll);
    return () => container.removeEventListener("scroll", onScroll);
  }, [hasMore, fetching, onLoadMore]);

  const renderPosts = () =>
    posts.map((post, index) => {
      const postId = getPostId(post);
      if (!postId) {
        return null;
      }
      return (
        <div
          className="swipe-feed__item"
          key={postId}
          data-feed-index={index}
        >
          <PostDialog
            simple
            immersive
            hideComments
            autoPlay
            isActive={activeIndex === index}
            postId={postId}
            postData={post}
            profileDispatch={onPostDialogDispatch}
          />
        </div>
      );
    });

  return (
    <div
      className="swipe-feed"
      ref={containerRef}
      style={{
        height: viewportHeight || undefined,
      }}
    >
      {posts.length > 0 ? (
        renderPosts()
      ) : fetching ? (
        <div className="swipe-feed__placeholder">
          <SkeletonLoader
            animated
            style={{ height: "60vh", width: "100%", borderRadius: "1rem" }}
          />
        </div>
      ) : (
        <Fragment />
      )}
      {hasMore && fetching && posts.length > 0 && (
        <div className="swipe-feed__loader">
          <SkeletonLoader
            animated
            style={{ height: "8rem", width: "8rem", borderRadius: "50%" }}
          />
        </div>
      )}
    </div>
  );
};

SwipeFeed.propTypes = {
  posts: PropTypes.arrayOf(PropTypes.object).isRequired,
  fetching: PropTypes.bool.isRequired,
  hasMore: PropTypes.bool.isRequired,
  onLoadMore: PropTypes.func.isRequired,
  onPostDialogDispatch: PropTypes.func.isRequired,
};

export default SwipeFeed;
